import { z } from 'zod';
import type { IAPI } from '../../apiResponse';
import { createApiResponseSchema } from '../../apiResponse';

const dailyTotalsSchema = z.object({
  totalPassed: z.number(),
  totalFailed: z.number(),
  totalSkipped: z.number(),
  totalTimeSpent: z.number(),
  totalReviews: z.number(),
  totalSessions: z.number(),
});

const dailyTrendRowSchema = z.object({
  date: z.string(),
  uniqueUsers: z.number(),
  totalReviews: z.number(),
  passed: z.number(),
  failed: z.number(),
  skipped: z.number(),
  totalTimeSpent: z.number(),
});

const dailyCountSchema = z.object({ date: z.string(), count: z.number() });

const dailyRevenueSchema = z.object({
  date: z.string(),
  revenue: z.number(),
  transactions: z.number(),
});

const subscriptionMatrixRowSchema = z.object({
  planKey: z.string(),
  total: z.number(),
  thisMonth: z.number(),
  thisWeek: z.number(),
  today: z.number(),
});

const deviceDistributionSchema = z.object({
  platform: z.string(),
  count: z.number(),
});

const retentionCohortSchema = z.object({
  rate: z.number(),
  retained: z.number(),
  cohortSize: z.number(),
});

const overviewDataSchema = z.object({
  users: z.object({
    total: z.number(),
    activeToday: z.number(),
    activeWeek: z.number(),
  }),
  learning: z.object({
    today: dailyTotalsSchema,
    week: dailyTotalsSchema,
    passRate: z.number(),
    month: dailyTotalsSchema,
  }),
  content: z.object({
    challenges: z.object({
      total: z.number(),
      published: z.number(),
      draft: z.number(),
      inTriage: z.number(),
    }),
    clips: z.object({
      total: z.number(),
      published: z.number(),
      pendingTriage: z.number(),
      approved: z.number(),
      rejected: z.number(),
    }),
  }),
  billing: z.object({
    subscriptions: z.object({
      total: z.number(),
      byPlan: z.array(z.object({ planKey: z.string(), count: z.number() })),
      byPlatform: z.array(z.object({ platform: z.string(), count: z.number() })),
    }),
    revenue: z.object({
      totalUsd: z.number(),
      transactionCount: z.number(),
    }),
  }),
  support: z.object({
    openTickets: z.number(),
  }),
  charts: z.object({
    dailySignups: z.array(dailyCountSchema),
    dailyTrend: z.array(dailyTrendRowSchema),
    dailySubscriptions: z.array(dailyCountSchema),
    dailyRevenue: z.array(dailyRevenueSchema),
  }),
  subscriptionMatrix: z.array(subscriptionMatrixRowSchema),
  totals: z.object({
    collections: z.number(),
    challengesSolved: z.number(),
    translations: z.number(),
  }),
  deviceDistribution: z.array(deviceDistributionSchema),
  retention: z.object({ d7: retentionCohortSchema, d30: retentionCohortSchema }).optional(),
});

export type IGetOverviewResponseData = z.infer<typeof overviewDataSchema>;

export const getOverviewResponseSchema = createApiResponseSchema(overviewDataSchema) as z.ZodSchema<
  IAPI<IGetOverviewResponseData>
>;

export type IGetOverviewResponse = IAPI<IGetOverviewResponseData>;
